/**
 * Runtime Validation Utilities
 * 
 * Since we have strict no-unsafe-* ESLint rules, we need proper validation
 * for API responses. This is a lightweight alternative to zod.
 * 
 * Pattern: Validator functions return typed Result or throw ValidationError.
 */

export class ValidationError extends Error {
  constructor(
    message: string,
    public readonly path: string,
    public readonly expected: string,
    public readonly received: unknown,
  ) {
    super(`Validation failed at '${path}': ${message}`);
    this.name = 'ValidationError';
  }
}

export type TValidationResult<T> =
  | { readonly success: true; readonly data: T }
  | { readonly success: false; readonly error: ValidationError };

// ============================================================================
// Primitive Validators
// ============================================================================

export function isString(value: unknown): value is string {
  return typeof value === 'string';
}

export function isNumber(value: unknown): value is number {
  return typeof value === 'number' && !Number.isNaN(value);
}

export function isBoolean(value: unknown): value is boolean {
  return typeof value === 'boolean';
}

export function isNull(value: unknown): value is null {
  return value === null;
}

export function isUndefined(value: unknown): value is undefined {
  return value === undefined;
}

export function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function isArray(value: unknown): value is unknown[] {
  return Array.isArray(value);
}

// ============================================================================
// Validator Combinators
// ============================================================================

export function assertString(value: unknown, path: string): string {
  if (!isString(value)) {
    throw new ValidationError('Expected string', path, 'string', typeof value);
  }
  return value;
}

export function assertNumber(value: unknown, path: string): number {
  if (!isNumber(value)) {
    throw new ValidationError('Expected number', path, 'number', typeof value);
  }
  return value;
}

export function assertBoolean(value: unknown, path: string): boolean {
  if (!isBoolean(value)) {
    throw new ValidationError('Expected boolean', path, 'boolean', typeof value);
  }
  return value;
}

export function assertObject(value: unknown, path: string): Record<string, unknown> {
  if (!isObject(value)) {
    throw new ValidationError('Expected object', path, 'object', typeof value);
  }
  return value;
}

export function assertArray(value: unknown, path: string): unknown[] {
  if (!isArray(value)) {
    throw new ValidationError('Expected array', path, 'array', typeof value);
  }
  return value;
}

// ============================================================================
// Идентификаторы
// ============================================================================

/**
 * Идентификатор сущности с провода.
 *
 * Норма: spec/features/app-shell/dto-conventions.md, R04 — у идентификатора
 * одного рода одна форма во всём обмене, числовой идентификатор приходит
 * числом, и знание «где какая форма ожидается» клиенту не вменяется.
 * Поэтому разбор идентификатора не зависит от того, пришло число или его
 * строковая запись: и то, и другое — один и тот же идентификатор, приводимый
 * к единому виду с отсечением крайних пробелов (терпимость приёма, R04).
 *
 * Составные идентификаторы, объявленные нормой второй формой адресации
 * («схема.таблица» у выборки данных), проходят тем же путём: строка остаётся
 * строкой.
 *
 * Пустое значение идентификатором не является — это повреждённый контракт, а
 * не отсутствие значения (для отсутствия есть варианты `…OrNull`).
 */
export function assertEntityId(value: unknown, path: string): string {
  if (isNumber(value)) {
    if (!Number.isFinite(value)) {
      throw new ValidationError(
        'Expected identifier',
        path,
        'number | string',
        value,
      );
    }
    return String(value);
  }
  if (isString(value)) {
    const trimmed = value.trim();
    if (trimmed.length > 0) return trimmed;
  }
  throw new ValidationError(
    'Expected identifier',
    path,
    'number | string',
    typeof value,
  );
}

/** Идентификатор либо явное отсутствие значения (dto-conventions-R03). */
export function assertEntityIdOrNull(
  value: unknown,
  path: string,
): string | null {
  if (isNull(value)) return null;
  return assertEntityId(value, path);
}

/**
 * Идентификатор либо отсутствие значения в любом виде: «нет ключа» и `null`
 * клиент трактует одинаково (dto-conventions-R03).
 */
export function assertOptionalEntityId(
  value: unknown,
  path: string,
): string | undefined {
  if (value === null || value === undefined) return undefined;
  return assertEntityId(value, path);
}

/** То же отсутствие значения, но выраженное как `null` (R03). */
export function assertEntityIdOrNullish(
  value: unknown,
  path: string,
): string | null {
  if (value === null || value === undefined) return null;
  return assertEntityId(value, path);
}

export function assertStringOrNull(value: unknown, path: string): string | null {
  if (isNull(value)) return null;
  return assertString(value, path);
}

export function assertNumberOrNull(value: unknown, path: string): number | null {
  if (isNull(value)) return null;
  return assertNumber(value, path);
}

export function assertOptionalString(value: unknown, path: string): string | undefined {
  if (value === null || value === undefined) return undefined;
  return assertString(value, path);
}

export function assertOptionalNumber(value: unknown, path: string): number | undefined {
  if (value === null || value === undefined) return undefined;
  return assertNumber(value, path);
}

export function assertOptionalBoolean(value: unknown, path: string): boolean | undefined {
  if (value === null || value === undefined) return undefined;
  return assertBoolean(value, path);
}

export function assertOptionalEnum<T extends string>(
  value: unknown,
  enumValues: readonly T[],
  path: string,
): T | undefined {
  if (value === null || value === undefined) return undefined;
  return assertEnum(value, enumValues, path);
}

export function assertStringOrNullish(value: unknown, path: string): string | null {
  if (value === null || value === undefined) return null;
  return assertString(value, path);
}

export function assertEnum<T extends string>(
  value: unknown,
  enumValues: readonly T[],
  path: string,
): T {
  const str = assertString(value, path);
  if (!enumValues.includes(str as T)) {
    throw new ValidationError(
      `Expected one of: ${enumValues.join(', ')}`,
      path,
      enumValues.join(' | '),
      str,
    );
  }
  return str as T;
}

export function assertOptionalStringRecord(
  value: unknown,
  path: string,
): Record<string, string> | undefined {
  if (value === null || value === undefined) return undefined;
  const obj = assertObject(value, path);
  const result: Record<string, string> = {};
  for (const [key, val] of Object.entries(obj)) {
    result[key] = assertString(val, `${path}.${key}`);
  }
  return result;
}

/**
 * Параметры подстановки отказа (api-errors-R06): объект со значениями
 * string|number, либо `null`, когда отказа/подстановок нет.
 * Значение недопустимого типа — сигнал повреждённого контракта API,
 * поэтому валидация падает, а не молча отбрасывает поле (тот же принцип,
 * что и у `assertOptionalStringRecord`).
 */
export function assertErrorParamsOrNull(
  value: unknown,
  path: string,
): Record<string, string | number> | null {
  if (value === null || value === undefined) return null;
  const obj = assertObject(value, path);
  const result: Record<string, string | number> = {};
  for (const [key, val] of Object.entries(obj)) {
    if (typeof val === 'string' || typeof val === 'number') {
      result[key] = val;
      continue;
    }
    throw new ValidationError(
      'Expected string or number',
      `${path}.${key}`,
      'string | number',
      typeof val,
    );
  }
  return result;
}

export function assertArrayOf<T>(
  value: unknown,
  itemValidator: (item: unknown, path: string) => T,
  path: string,
): T[] {
  const arr = assertArray(value, path);
  return arr.map((item, index) => itemValidator(item, `${path}[${index}]`));
}

// ============================================================================
// Safe Validation Wrapper
// ============================================================================

export function safeValidate<T>(
  value: unknown,
  validator: (data: unknown) => T,
): TValidationResult<T> {
  try {
    const data = validator(value);
    return { success: true, data };
  } catch (error) {
    if (error instanceof ValidationError) {
      return { success: false, error };
    }
    throw error;
  }
}

